/**
 * Error Boundary Component
 * Catches JavaScript errors in child component tree and displays fallback UI
 *
 * @module components/ErrorBoundary
 */

import React, { Component, ErrorInfo, ReactNode } from 'react';

// =============================================================================
// Types
// =============================================================================

interface ErrorBoundaryProps {
    /** Child components to render */
    children: ReactNode;
    /** Optional custom fallback UI */
    fallback?: ReactNode;
    /** Optional error handler callback */
    onError?: (error: Error, errorInfo: ErrorInfo) => void;
    /** Title shown in error UI */
    title?: string;
}

interface ErrorBoundaryState {
    hasError: boolean;
    error: Error | null;
    errorInfo: ErrorInfo | null;
}

// =============================================================================
// Component
// =============================================================================

/**
 * Error Boundary catches runtime errors and displays a fallback UI
 *
 * @example
 * ```tsx
 * <ErrorBoundary fallback={<div>Something went wrong</div>}>
 *   <MyComponent />
 * </ErrorBoundary>
 * ```
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
        };
    }

    static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
        // Log error to console
        console.error('ErrorBoundary caught an error:', error, errorInfo);

        // Update state with error info
        this.setState({ errorInfo });

        // Call optional error handler
        this.props.onError?.(error, errorInfo);
    }

    handleReset = (): void => {
        this.setState({
            hasError: false,
            error: null,
            errorInfo: null,
        });
    };

    handleReload = (): void => {
        window.location.reload();
    };

    render(): ReactNode {
        const { hasError, error, errorInfo } = this.state;
        const { children, fallback, title = 'Something went wrong' } = this.props;

        if (hasError) {
            // Use custom fallback if provided
            if (fallback) {
                return fallback;
            }

            // Default error UI
            return (
                <div style={styles.container}>
                    <div style={styles.content}>
                        {/* Error Icon */}
                        <div style={styles.iconContainer}>
                            <svg
                                width="64"
                                height="64"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                style={{ color: '#ef4444' }}
                            >
                                <circle cx="12" cy="12" r="10" />
                                <line x1="12" y1="8" x2="12" y2="12" />
                                <line x1="12" y1="16" x2="12.01" y2="16" />
                            </svg>
                        </div>

                        {/* Title */}
                        <h1 style={styles.title}>{title}</h1>

                        {/* Error Message */}
                        <p style={styles.message}>
                            {error?.message || 'An unexpected error occurred'}
                        </p>

                        {/* Action Buttons */}
                        <div style={styles.buttonContainer}>
                            <button onClick={this.handleReset} style={styles.button}>
                                Try Again
                            </button>
                            <button
                                onClick={this.handleReload}
                                style={{ ...styles.button, ...styles.secondaryButton }}
                            >
                                Reload Page
                            </button>
                        </div>

                        {/* Technical Details (Development Only) */}
                        {process.env.NODE_ENV === 'development' && errorInfo && (
                            <details style={styles.details}>
                                <summary style={styles.summary}>Technical Details</summary>
                                <pre style={styles.stack}>
                                    {error?.stack}
                                    {'\n\nComponent Stack:'}
                                    {errorInfo.componentStack}
                                </pre>
                            </details>
                        )}
                    </div>
                </div>
            );
        }

        return children;
    }
}

// =============================================================================
// Styles
// =============================================================================

const styles: Record<string, React.CSSProperties> = {
    container: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '24px',
        backgroundColor: 'var(--bg-primary, #f8fafc)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    },
    content: {
        textAlign: 'center',
        maxWidth: '500px',
    },
    iconContainer: {
        marginBottom: '24px',
    },
    title: {
        fontSize: '24px',
        fontWeight: 600,
        color: 'var(--text-primary, #1e293b)',
        margin: '0 0 12px 0',
    },
    message: {
        fontSize: '16px',
        color: 'var(--text-secondary, #64748b)',
        margin: '0 0 24px 0',
        lineHeight: 1.5,
    },
    buttonContainer: {
        display: 'flex',
        gap: '12px',
        justifyContent: 'center',
        flexWrap: 'wrap',
    },
    button: {
        padding: '12px 24px',
        fontSize: '14px',
        fontWeight: 500,
        border: 'none',
        borderRadius: '8px',
        cursor: 'pointer',
        backgroundColor: 'var(--accent-primary, #3b82f6)',
        color: '#ffffff',
        transition: 'background-color 0.2s, transform 0.1s',
    },
    secondaryButton: {
        backgroundColor: 'transparent',
        color: 'var(--text-secondary, #64748b)',
        border: '1px solid var(--border-color, #e2e8f0)',
    },
    details: {
        marginTop: '32px',
        textAlign: 'left',
        backgroundColor: 'var(--bg-secondary, #f1f5f9)',
        borderRadius: '8px',
        padding: '16px',
    },
    summary: {
        cursor: 'pointer',
        fontWeight: 500,
        color: 'var(--text-primary, #1e293b)',
        marginBottom: '12px',
    },
    stack: {
        fontSize: '12px',
        color: 'var(--text-secondary, #64748b)',
        overflow: 'auto',
        maxHeight: '300px',
        padding: '12px',
        backgroundColor: 'var(--bg-primary, #ffffff)',
        borderRadius: '4px',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
    },
};

export default ErrorBoundary;
